import { useEffect, useMemo, useState } from "react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import InfoModal from "../../components/Modals/InfoModal";
import PageContainer from "../../components/PageContainer";
import Sidebar from "../../components/Sidebar";
import DepartmentBadge from "../../components/deparments/DepartmentBadge";
import ClientServiceLinks from "../../components/clientServiceSubscription/links/ClientServiceLinks";
import ClientServiceNotes from "../../components/clientServiceSubscription/notes/ClientServiceNotes";
import ClientServiceTemporaryNotes from "../../components/clientServiceSubscription/notes/ClientServiceTemporaryNotes";
import ClientServiceSubscriptionTasksList from "../../components/clientServiceSubscriptionTask/ClientServiceSubscriptionTasksList";
import AssignEmployeToTaskWhenServiceStarts from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/AssignEmployeeToTaskWhenServiceStarts";
import {
    getCompletionSubLabel as buildCompletionSubLabel,
    getDueDateStatusFromDate,
    getDueDateSubLabel as buildDueDateSubLabel,
} from "../../helpers/dueDateStatus";
import { formatDate } from "../../helpers/formatDate";
import { formatDateTime } from "../../helpers/formatDateTime";
import { formatPhoneNumber } from "../../helpers/formatPhoneNumber";
import { formatTaxNumber } from "../../helpers/formatTaxNumber";
import { getClientService } from "../../services/clientService/getClientService";
import { getClientSubscriptionsByClientId } from "../../services/clientService/getClientSubscriptionsByClientId";
import { updateClientServiceStatus } from "../../services/clientService/updateClientServiceStatus";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import { deleteSubscription } from "../../services/deleteSubscription";
import UpdateClientServiceModal from "./UpdateClientServiceModal";
import TeamRoleBadge from "../../components/teamRoles/TeamRoleBadge";
import {
    getTeamMemberRoleColorHex,
    getTeamMemberRoleName,
} from "../Teams/teamMembersUtils";
import { usePermissions } from "../../context/PermissionsContext";
import { getEfileStatusDisplayLabel } from "../../helpers/eFileStatus";
import { normalizeClientServiceInterval } from "../../helpers/intervalTypesMap";
import { useCelebration } from "../../context/CelebrationContext";
import { shouldCelebrateServiceCompletion } from "../../helpers/completionCelebration";
import { useClientInactivityConfirmation } from "../../context/ClientInactivityConfirmationContext";
import {
    getIsTeamAssignmentValid,
    getTeamAssignmentIssue,
    getTeamAssignmentIssueMessage,
} from "../../helpers/serviceTeamAssignment";
import { getConfirmedInactiveClientState } from "../../helpers/clientActivityTransition";
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

const DIVISION_CLASS = "relative w-full p-4 bg-white border shadow-sm rounded-xl";

const normalizeClientService = (service) => ({
    ...service,
    interval: normalizeClientServiceInterval(service?.interval),
    intervalType: normalizeClientServiceInterval(service?.intervalType),
    team: service?.team ?? service?.Team ?? null,
    assignedAt:
        service?.assignedAt ??
        service?.AssignedAt ??
        service?.assignedDate ??
        service?.AssignedDate ??
        null,
    updated:
        service?.updated ??
        service?.Updated ??
        service?.lastModifiedAt ??
        service?.LastModifiedAt ??
        null,
    completionDate:
        service?.completionDate ?? service?.CompletionDate ?? null,
    lastActivityAt:
        service?.lastActivityAt ?? service?.LastActivityAt ?? null,
    temporaryNotes: service?.temporaryNotes ?? service?.TemporaryNotes ?? "",
    serviceTemplateIsCurrent:
        service?.serviceTemplateIsCurrent ??
        service?.ServiceTemplateIsCurrent ??
        service?.serviceTemplate?.isCurrent ??
        service?.serviceTemplate?.IsCurrent ??
        true,
    serviceTemplateVersionNumber:
        service?.serviceTemplateVersionNumber ??
        service?.ServiceTemplateVersionNumber ??
        service?.serviceTemplate?.versionNumber ??
        service?.serviceTemplate?.VersionNumber ??
        1,
});

const getClientServiceId = (service) =>
    service?.id ??
    service?.Id ??
    service?.clientServiceSubscriptionId ??
    service?.ClientServiceSubscriptionId ??
    service?.clientSubscriptionId ??
    service?.ClientSubscriptionId ??
    null;

const findSubscriptionById = (subscriptions, clientServiceId) =>
    subscriptions.find(
        (subscription) =>
            String(getClientServiceId(subscription)) === String(clientServiceId),
    ) || null;

const getSafeText = (value, fallback = "-") => {
    if (value === null || value === undefined) return fallback;

    const text = typeof value === "string" ? value.trim() : String(value).trim();
    return text || fallback;
};

const getNormalizedStatus = (status) => {
    if (typeof status === "string") return status.trim().toLowerCase();
    return status;
};

const isServiceComplete = (status) => {
    const normalizedStatus = getNormalizedStatus(status);

    return (
        normalizedStatus === "complete" ||
        normalizedStatus === "completed" ||
        normalizedStatus === 2
    );
};

const getArrayValue = (value) => {
    if (Array.isArray(value)) return value;

    if (Array.isArray(value?.$values)) return value.$values;

    if (Array.isArray(value?.values)) return value.values;

    if (Array.isArray(value?.items)) return value.items;

    return [];
};

const getTeamMembers = (team) => {
    if (!team) return [];

    const members =
        team?.members ??
        team?.Members ??
        team?.teamMembers ??
        team?.TeamMembers ??
        team?.assignedMembers ??
        team?.AssignedMembers ??
        [];

    return getArrayValue(members);
};

const ClientService = () => {
    const navigate = useNavigate();
    const { requestInactiveConfirmation } = useClientInactivityConfirmation();
    const { can, permissionsLoading } = usePermissions();
    const { celebrate, showCompletionMessage } = useCelebration();
    const canReadServices = can("client_services.read");
    const canWriteServices = can("client_services.write");
    const canDeleteServices = can("client_services.delete");
    const canReadSteps = can("steps.read");
    const canWriteSteps = can("steps.write");
    const canDeleteSteps = can("steps.delete");
    const [updateClientServiceModalOpen, setUpdateClientServiceModalOpen] =
        useState(false);
    const [teamAssignmentRequired, setTeamAssignmentRequired] = useState(false);
    const { clientServiceId } = useParams();
    const [clientService, setClientService] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [initialLoadError, setInitialLoadError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [infoModal, setInfoModal] = useState(false);
    const [isPermanentFolderOpen, setIsPermanentFolderOpen] = useState(false);
    const [isModalStartServiceOpen, setIsModalStartServiceOpen] = useState(false);
    const [isServiceStatusInProgress, setIsServiceStatusInProgress] =
        useState(false);
    const clientId = useParams().clientId;
    const [loggedInEmployee, setLoggedInEmployee] = useState({});
    const getServiceTemplateMeta = (service) => {
        const isCurrent =
            service?.serviceTemplateIsCurrent ??
            service?.ServiceTemplateIsCurrent ??
            service?.serviceTemplate?.isCurrent ??
            service?.serviceTemplate?.IsCurrent ??
            true;
        const versionNumber =
            service?.serviceTemplateVersionNumber ??
            service?.ServiceTemplateVersionNumber ??
            service?.serviceTemplate?.versionNumber ??
            service?.serviceTemplate?.VersionNumber ??
            1;
        return { isCurrent, versionNumber };
    };

    const isCompletedSubscription = (service) => {
        return isServiceComplete(service?.status);
    };
    const getEmployeeId = () => loggedInEmployee?.id || loggedInEmployee?.employeeId;

    const getClientServiceWithPreservedTeam = async () => {
        if (!canReadServices) return null;

        const [clientServiceResponse, subscriptionsResponse] = await Promise.all([
            getClientService(clientServiceId),
            getClientSubscriptionsByClientId(clientId),
        ]);

        if (!clientServiceResponse.success) {
            throw new Error(
                clientServiceResponse.message ||
                    "Error fetching service details. Please try again.",
            );
        }

        const rawSubscriptions =
            subscriptionsResponse?.data?.clientServiceSubscriptions || [];

        console.log(
            "Loaded subscriptions raw:",
            JSON.stringify(rawSubscriptions, null, 2),
        );

        const matchingSubscription = findSubscriptionById(
            Array.isArray(rawSubscriptions) ? rawSubscriptions : [],
            clientServiceId,
        );
        const assignedTeam =
            matchingSubscription?.team ??
            matchingSubscription?.Team ??
            clientServiceResponse.data?.team ??
            clientServiceResponse.data?.Team ??
            null;
        const assignmentSource = matchingSubscription ?? clientServiceResponse.data;

        return normalizeClientService({
            ...clientServiceResponse.data,
            team: assignedTeam,
            isTeamAssignmentValid:
                getIsTeamAssignmentValid(clientServiceResponse.data) ??
                getIsTeamAssignmentValid(assignmentSource),
            teamAssignmentIssue:
                getTeamAssignmentIssue(clientServiceResponse.data) ||
                getTeamAssignmentIssue(assignmentSource),
        });
    };

    const newClientService = async () => {
        if (!canReadServices) return null;

        try {
            const normalizedClientService =
                await getClientServiceWithPreservedTeam();
            setClientService(normalizedClientService);
            checkIfServiceIsInProgress(normalizedClientService.status);
            console.log("neClient", normalizedClientService);
            return normalizedClientService;
        } catch (error) {
            toast.error(
                getClientServiceMessage(
                    error.message,
                    "Error fetching service details. Please try again.",
                ),
            );
            return null;
        }
    };

    const onTeamAssignmentSaveSuccess = async () => {
        const refreshedService = await newClientService();
        const assignmentIsValid =
            getIsTeamAssignmentValid(refreshedService) === true;

        if (assignmentIsValid) {
            setTeamAssignmentRequired(false);
            return true;
        }

        setTeamAssignmentRequired(true);
        const issue = getTeamAssignmentIssueMessage(refreshedService);
        toast.error(
            issue ||
                "The service still does not have a valid team assignment. Please correct the team configuration before continuing.",
        );
        return false;
    };

    const onDeleteService = async (serviceId) => {
        if (!canDeleteServices) {
            return {
                success: false,
                message: "You don't have permission to delete Client Services.",
            };
        }

        const response = await deleteSubscription(serviceId);
        if (response?.data?.success !== true) {
            return {
                success: false,
                message: getClientServiceMessage(
                    response?.data?.message || response?.message,
                    "The service could not be deleted. Please try again.",
                ),
            };
        }

        setUpdateClientServiceModalOpen(false);
        const confirmationResult = await requestInactiveConfirmation(
            response.data?.clientActivityTransition,
        );
        const confirmedInactiveClientState =
            getConfirmedInactiveClientState(confirmationResult);
        navigate(`/clients/manage/${clientId}`, {
            state: confirmedInactiveClientState
                ? { confirmedInactiveClientState }
                : undefined,
        });
        return { success: true };
    };

    useEffect(() => {
        setTeamAssignmentRequired(false);
        setUpdateClientServiceModalOpen(false);
        setClientService(undefined);
    }, [clientServiceId]);

    useEffect(() => {
        if (
            clientService !== undefined &&
            String(getClientServiceId(clientService)) === String(clientServiceId)
        ) {
            console.log(
                "ClientService received:",
                JSON.stringify(clientService, null, 2),
            );
            if (
                getIsTeamAssignmentValid(clientService) === false &&
                canWriteServices
            ) {
                setTeamAssignmentRequired(true);
                setUpdateClientServiceModalOpen(true);
            }
        } else {
            return;
        }
    }, [clientService, canWriteServices]);

    useEffect(() => {
        let isMounted = true;

        const loadInitialPageData = async () => {
            if (permissionsLoading) return;

            if (!canReadServices) {
                if (isMounted) {
                    setClientService(undefined);
                    setIsLoading(false);
                }
                return;
            }

            setIsLoading(true);
            setInitialLoadError(null);

            try {
                const [normalizedClientService, currentEmployeeResponse] =
                    await Promise.all([
                        getClientServiceWithPreservedTeam(),
                        getLoggedInEmployee(),
                    ]);

                if (!currentEmployeeResponse.success) {
                    throw new Error(
                        currentEmployeeResponse.message ||
                            "Error fetching logged-in employee.",
                    );
                }

                if (!isMounted) {
                    return;
                }

                setClientService(normalizedClientService);
                checkIfServiceIsInProgress(normalizedClientService.status);
                setLoggedInEmployee(currentEmployeeResponse.data.employeeResponse);
            } catch (error) {
                console.error("Error loading client service page:", error);
                if (!isMounted) {
                    return;
                }
                setInitialLoadError(error);
                toast.error(
                    error.message ||
                        "Error fetching service details. Please try again.",
                );
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadInitialPageData();

        return () => {
            isMounted = false;
        };
    }, [clientServiceId, canReadServices, permissionsLoading]);

    const onClickStartService = () => {
        if (!canWriteServices) return;
        setIsModalStartServiceOpen(true);
    };

    const checkIfServiceIsInProgress = (status) => {
        if (status !== "To Do") {
            console.log(status);
            setIsServiceStatusInProgress(true);
        }
        return;
    };

    const onHandleUpdateClientServiceStatusComplete = async (
        { showSuccessToast = true } = {},
    ) => {
        if (!canWriteServices) return;

        const employeeId = getEmployeeId();

        if (!employeeId) {
            toast.error("Logged in employee not loaded yet. Try again.");
            return;
        }

        const completionDate = new Date().toISOString();

        const response = await updateClientServiceStatus(clientServiceId, 2, {
            completionDate,
            completedBy: loggedInEmployee,
            lastModifiedBy: loggedInEmployee,
        });

        if (!response.success) {
            toast.error(
                getClientServiceMessage(
                    response.message,
                    "The service could not be completed.",
                ),
            );
            return;
        }

        const confirmationResult = await requestInactiveConfirmation(
            response.data?.clientActivityTransition,
        );
        const confirmedInactiveClientState =
            getConfirmedInactiveClientState(confirmationResult);

        if (shouldCelebrateServiceCompletion(response)) {
            celebrate();
            showCompletionMessage(
                "Congratulations on completing the service",
            );
        }

        if (showSuccessToast) {
            toast.success(
                getClientServiceMessage(
                    response.data.message,
                    "Service completed successfully.",
                ),
            );
        }

        setClientService((prev) => ({
            ...prev,
            status: "Complete",
            completionDate,
            completedBy: loggedInEmployee,
            lastModifiedBy: loggedInEmployee,
        }));

        navigate(`/clients/manage/${clientId}`, {
            state: {
                refresh: true,
                completedServiceId: clientServiceId,
                ...(confirmedInactiveClientState
                    ? { confirmedInactiveClientState }
                    : {}),
            },
        });
    };

    const isComplete = isServiceComplete(clientService?.status);
    const dueDateBadgeColorClass = isComplete
        ? "bg-slate-500"
        : getDueDateStatusFromDate(clientService?.endDate)?.bgClass ||
          "bg-slate-400";
    const dueDateSubLabel = buildDueDateSubLabel(
        clientService?.endDate,
        clientService?.status,
    );
    const completionSubLabel = buildCompletionSubLabel(
        clientService?.endDate,
        clientService?.completionDate,
        clientService?.status,
    );
    const clientServiceTeams = useMemo(() => {
        const assignedTeam = clientService?.team || clientService?.Team || null;
        return assignedTeam ? [assignedTeam] : [];
    }, [clientService]);
    const assignedClientServiceTeam =
        clientService?.team || clientService?.Team || null;

    console.log(
        "ClientService assigned team:",
        JSON.stringify(assignedClientServiceTeam, null, 2),
    );
    console.log(
        "Resolved assigned team members:",
        getTeamMembers(assignedClientServiceTeam),
    );

    return (
        <section>
            <Toaster />
            {canWriteServices && (
                <AssignEmployeToTaskWhenServiceStarts
                    open={isModalStartServiceOpen}
                    setOpen={setIsModalStartServiceOpen}
                    loggedInEmployee={loggedInEmployee}
                    clientSubscriptionId={clientServiceId}
                    newClientService={newClientService}
                    clientId={clientId}
                />
            )}
            <InfoModal infoModal={infoModal} setInfoModal={setInfoModal} />
            {canWriteServices && (
                <UpdateClientServiceModal
                    clientService={clientService}
                    setClientService={setClientService}
                    open={updateClientServiceModalOpen}
                    setOpen={setUpdateClientServiceModalOpen}
                    clientId={clientId}
                    newClientService={newClientService}
                    loggedInEmployee={loggedInEmployee}
                    teamAssignmentRequired={teamAssignmentRequired}
                    onSaveSuccess={onTeamAssignmentSaveSuccess}
                    canDeleteService={canDeleteServices}
                    onDeleteService={onDeleteService}
                    onManageClientTeams={() =>
                        navigate(`/clients/manage/${clientId}`, {
                            state: { openClientTeams: true },
                        })
                    }
                />
            )}
            {/* Sidebar */}
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                {/* Content area */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    {/*  Site header */}
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />
                    <PageContainer
                        isLoading={permissionsLoading || isLoading}
                        error={initialLoadError}
                        loadingText="Loading service details..."
                    >
                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start">
                                    {!permissionsLoading &&
                                    !canReadServices ? (
                                        <div className="w-full p-4 mt-8 text-sm bg-white border rounded-sm text-slate-500 border-slate-200">
                                            You don't have permission to view client
                                            services.
                                        </div>
                                    ) : clientService ? (
                                        <>
                                            {getIsTeamAssignmentValid(clientService) ===
                                                false && !canWriteServices ? (
                                                <div className="mb-4 w-full rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                                    This service requires a team assignment.
                                                    A user with permission to edit services
                                                    must complete this information.
                                                </div>
                                            ) : null}
                                            <Breadcrumbs
                                                home={`/clients`}
                                                pages={[
                                                    {
                                                        name: `${clientService.clientName}`,
                                                        href: `/clients/manage/${clientId}`,
                                                    },
                                                    {
                                                        name: `${clientService.serviceName}`,
                                                        href: `/clients/service/${clientService.id}/client/${clientId}`,
                                                        current: true,
                                                    },
                                                ]}
                                            />

                                            <div className="w-full">
                                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                                    <div className="min-w-0">
                                                        <h1 className="text-2xl font-bold text-left md:text-2xl text-slate-800">
                                                            <span className="inline-flex flex-wrap items-center gap-2">
                                                                <span>
                                                                    Service:{" "}
                                                                    {getSafeText(
                                                                        clientService.serviceName,
                                                                    )}
                                                                </span>
                                                                {(() => {
                                                                    const {
                                                                        isCurrent,
                                                                        versionNumber,
                                                                    } =
                                                                        getServiceTemplateMeta(
                                                                            clientService,
                                                                        );
                                                                    const showLegacyBadge =
                                                                        isCompletedSubscription(
                                                                            clientService,
                                                                        ) &&
                                                                        !isCurrent;
                                                                    return showLegacyBadge ? (
                                                                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                                                            Legacy v
                                                                            {
                                                                                versionNumber
                                                                            }
                                                                        </span>
                                                                    ) : null;
                                                                })()}
                                                            </span>
                                                        </h1>

                                                        <div className="flex flex-wrap items-start gap-4 mt-3">
                                                            <div>
                                                                <p
                                                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white ${dueDateBadgeColorClass}`}
                                                                >
                                                                    Due Date:{" "}
                                                                    {clientService.endDate
                                                                        ? formatDate(
                                                                              clientService.endDate,
                                                                              {
                                                                                  dateStyle:
                                                                                      "short",
                                                                              },
                                                                          )
                                                                        : "Not assigned"}
                                                                </p>

                                                                {!isComplete &&
                                                                dueDateSubLabel ? (
                                                                    <span className="block mt-2 text-xs font-medium text-slate-600">
                                                                        {
                                                                            dueDateSubLabel
                                                                        }
                                                                    </span>
                                                                ) : null}

                                                                {isComplete &&
                                                                completionSubLabel ? (
                                                                    <span className="block mt-2 text-xs font-medium text-slate-600">
                                                                        {
                                                                            completionSubLabel
                                                                        }
                                                                    </span>
                                                                ) : null}
                                                            </div>

                                                            <div className="flex flex-col items-start">
                                                                <div className="flex flex-wrap items-center gap-2">
                                                                    <span
                                                                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-md font-medium text-white ${
                                                                            clientService.status ===
                                                                            "To Do"
                                                                                ? "bg-red-500"
                                                                                : clientService.status ==
                                                                                    "In Progress"
                                                                                  ? "bg-yellow-500"
                                                                                  : "bg-teal-500"
                                                                        }`}
                                                                    >
                                                                        {getSafeText(
                                                                            clientService.status,
                                                                        )}
                                                                    </span>

                                                                    {clientService.status ==
                                                                        "To Do" &&
                                                                        canWriteServices && (
                                                                        <button
                                                                            className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                                                                            onClick={() =>
                                                                                onClickStartService()
                                                                            }
                                                                        >
                                                                            Start
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                <span className="block mt-2 text-xs font-medium text-slate-600">
                                                                    {getEfileStatusDisplayLabel(
                                                                        clientService,
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {canWriteServices && (
                                                        <button
                                                            onClick={() => {
                                                                if (!canWriteServices)
                                                                    return;
                                                                setTeamAssignmentRequired(
                                                                    false,
                                                                );
                                                                setUpdateClientServiceModalOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            className="h-10 text-white bg-teal-500 btn hover:bg-teal-600"
                                                        >
                                                            Edit Service
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="grid items-stretch grid-cols-1 gap-4 mt-6 lg:grid-cols-12">
                                                    <div className="h-full lg:col-span-3">
                                                        <div className="h-full p-4 bg-white border shadow-sm rounded-xl">
                                                            <h2 className="text-sm font-bold text-slate-800">
                                                                Client Info
                                                            </h2>

                                                            <div className="grid grid-cols-1 mt-3 gap-x-6 gap-y-2">
                                                                <InfoRow
                                                                    label="Client"
                                                                    value={getSafeText(
                                                                        clientService.clientName,
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Email"
                                                                    value={getSafeText(
                                                                        clientService.clientEmail,
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Phone"
                                                                    value={formatPhoneNumber(
                                                                        clientService.clientPhone,
                                                                        "-",
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Tax ID Number"
                                                                    value={getSafeText(
                                                                        formatTaxNumber(
                                                                            {
                                                                                clientType:
                                                                                    clientService.clientType ??
                                                                                    clientService.ClientType,
                                                                                taxNumber:
                                                                                    clientService.taxNumber ??
                                                                                    clientService.TaxNumber,
                                                                            },
                                                                        ),
                                                                        "Not assigned",
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="h-full lg:col-span-6">
                                                        <div className="h-full p-4 bg-white border shadow-sm rounded-xl">
                                                            <h2 className="text-sm font-bold text-slate-800">
                                                                Service Info
                                                            </h2>

                                                            <div className="grid grid-cols-1 mt-3 gap-x-6 gap-y-2 sm:grid-cols-2">
                                                                <InfoRow
                                                                    label="Interval"
                                                                    value={getSafeText(
                                                                        clientService.intervalType,
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Assigned Manager"
                                                                    value={
                                                                        clientService.team
                                                                            ? getSafeText(
                                                                                  clientService
                                                                                      .team
                                                                                      .managerName,
                                                                                  "Not assigned",
                                                                              )
                                                                            : "Not assigned"
                                                                    }
                                                                />
                                                                <InfoRow
                                                                    label="Year"
                                                                    value={getSafeText(
                                                                        clientService.year,
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Assigned By"
                                                                    value={getEmployeeDisplayName(
                                                                        clientService.assignedBy,
                                                                        "Not assigned",
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Date assigned"
                                                                    value={getSafeText(
                                                                        formatDateTime(
                                                                            clientService.assignedAt,
                                                                        ),
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Last Modified By"
                                                                    value={getEmployeeDisplayName(
                                                                        clientService.lastModifiedBy,
                                                                        "Not assigned",
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Last Modified At"
                                                                    value={getSafeText(
                                                                        formatDateTime(
                                                                            clientService.updated,
                                                                        ),
                                                                    )}
                                                                />
                                                                <InfoRow
                                                                    label="Completion Date"
                                                                    value={
                                                                        clientService.completionDate
                                                                            ? formatDateTime(
                                                                                  clientService.completionDate,
                                                                              )
                                                                            : "Not completed"
                                                                    }
                                                                />
                                                                <InfoRow
                                                                    label="Completed By"
                                                                    value={getEmployeeDisplayName(
                                                                        clientService.completedBy,
                                                                        "Not completed",
                                                                    )}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="h-full lg:col-span-3">
                                                        <div className="flex flex-col h-full p-4 bg-white border shadow-sm rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <h2 className="text-sm font-bold text-slate-800">
                                                                    Teams
                                                                </h2>
                                                                <span className="text-xs font-medium text-slate-500">
                                                                    {
                                                                        clientServiceTeams.length
                                                                    }
                                                                </span>
                                                            </div>

                                                            {clientServiceTeams.length ? (
                                                                <ul className="mt-3 space-y-2 overflow-auto">
                                                                    {clientServiceTeams.map(
                                                                        (
                                                                            team,
                                                                            index,
                                                                        ) => {
                                                                            const deptName =
                                                                                getTeamDepartmentName(
                                                                                    team,
                                                                                );
                                                                            const deptColorHex =
                                                                                getTeamDepartmentColor(
                                                                                    team,
                                                                                );

                                                                            return (
                                                                                <li
                                                                                    key={
                                                                                        team?.id ??
                                                                                        team?.Id ??
                                                                                        getSafeText(
                                                                                            team?.name ??
                                                                                                team?.Name,
                                                                                        ).concat(
                                                                                            `-${index}`,
                                                                                        )
                                                                                    }
                                                                                    className="p-2 rounded-lg bg-slate-50"
                                                                                >
                                                                                    <div className="min-w-0">
                                                                                        <div className="flex items-center gap-2">
                                                                                            {deptName ? (
                                                                                                <DepartmentBadge
                                                                                                    name={
                                                                                                        deptName
                                                                                                    }
                                                                                                    colorHex={
                                                                                                        deptColorHex
                                                                                                    }
                                                                                                />
                                                                                            ) : null}
                                                                                            <span className="text-sm font-medium truncate text-slate-800">
                                                                                                {getSafeText(
                                                                                                    team?.name ??
                                                                                                        team?.Name,
                                                                                                    "Not assigned",
                                                                                                )}
                                                                                            </span>
                                                                                        </div>

                                                                                        <TeamMembersList
                                                                                            members={getTeamMembers(
                                                                                                team,
                                                                                            )}
                                                                                        />
                                                                                    </div>
                                                                                </li>
                                                                            );
                                                                        },
                                                                    )}
                                                                </ul>
                                                            ) : (
                                                                <p className="mt-3 text-sm text-slate-800">
                                                                    No teams assigned
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                className={`${DIVISION_CLASS} mt-6`}
                                            >
                                                <div className="border rounded-md border-slate-200 bg-slate-50">
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setIsPermanentFolderOpen(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                        className="flex items-center justify-between w-full px-4 py-3 text-left"
                                                        aria-expanded={
                                                            isPermanentFolderOpen
                                                        }
                                                    >
                                                        <span className="text-lg font-semibold text-slate-800">
                                                            Permanent Folder
                                                        </span>

                                                        {isPermanentFolderOpen ? (
                                                            <MinusIcon
                                                                className="w-5 h-5 text-slate-600"
                                                                aria-hidden="true"
                                                            />
                                                        ) : (
                                                            <PlusIcon
                                                                className="w-5 h-5 text-slate-600"
                                                                aria-hidden="true"
                                                            />
                                                        )}
                                                    </button>

                                                    {isPermanentFolderOpen && (
                                                        <div className="p-4 space-y-4 bg-white border-t border-slate-200">
                                                            <ClientServiceNotes
                                                                clientService={
                                                                    clientService
                                                                }
                                                                setClientService={
                                                                    setClientService
                                                                }
                                                                loggedInEmployee={
                                                                    loggedInEmployee
                                                                }
                                                            />

                                                            <div className="pt-4 border-t border-slate-200">
                                                                <ClientServiceLinks
                                                                    clientService={
                                                                        clientService
                                                                    }
                                                                    setClientService={
                                                                        setClientService
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div
                                                className={`${DIVISION_CLASS} mt-4`}
                                            >
                                                <ClientServiceTemporaryNotes
                                                    clientService={clientService}
                                                    setClientService={
                                                        setClientService
                                                    }
                                                    loggedInEmployee={
                                                        loggedInEmployee
                                                    }
                                                />
                                            </div>

                                            <div
                                                className={`${DIVISION_CLASS} mt-4`}
                                            >
                                                {canReadSteps ? (
                                                    <ClientServiceSubscriptionTasksList
                                                        clientServiceId={
                                                            clientServiceId
                                                        }
                                                        onHandleUpdateClientServiceStatusComplete={
                                                            onHandleUpdateClientServiceStatusComplete
                                                        }
                                                        subscriptionInterval={
                                                            clientService.intervalType
                                                        }
                                                        isServiceStatusInProgress={
                                                            isServiceStatusInProgress
                                                        }
                                                        clientService={
                                                            clientService
                                                        }
                                                        updateClientService={
                                                            newClientService
                                                        }
                                                        clientId={clientId}
                                                        canReadSteps={
                                                            canReadSteps
                                                        }
                                                        canWriteSteps={
                                                            canWriteSteps
                                                        }
                                                        canDeleteSteps={
                                                            canDeleteSteps
                                                        }
                                                        canWriteServices={
                                                            canWriteServices
                                                        }
                                                    />
                                                ) : (
                                                    <div className="text-sm text-slate-500">
                                                        You don't have permission
                                                        to view Steps.
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        </main>
                    </PageContainer>
                </div>
            </div>
        </section>
    );
};

function InfoRow({ label, value }) {
    const text = getSafeText(value);

    return (
        <div>
            <div className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                {label}
            </div>
            <div className="mt-1 text-sm break-words text-slate-800">{text}</div>
        </div>
    );
}

function TeamMembersList({ members }) {
    const list = getArrayValue(members);

    if (!list.length) {
        return (
            <p className="mt-2 ml-1 text-xs text-slate-500">
                No team members assigned
            </p>
        );
    }

    return (
        <div className="mt-2 ml-1 space-y-1">
            {list.map((member, index) => {
                const fullName = getTeamMemberDisplayName(member);
                const roleName = getTeamMemberRoleName(member);
                const roleColorHex = getTeamMemberRoleColorHex(member);

                return (
                    <div
                        key={
                            member?.employeeId ??
                            member?.EmployeeId ??
                            member?.id ??
                            member?.Id ??
                            `${fullName}-${index}`
                        }
                        className="flex items-center justify-between gap-2 text-xs"
                    >
                        <span
                            className="min-w-0 truncate text-slate-600"
                            title={fullName}
                        >
                            {fullName}
                        </span>
                        {roleName ? (
                            <TeamRoleBadge
                                name={roleName}
                                colorHex={roleColorHex}
                                className="shrink-0"
                            />
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
}

function getTeamMemberDisplayName(member) {
    if (!member) return "-";

    const employee = member.employee ?? member.Employee ?? null;

    const fullName =
        member.fullName ??
        member.FullName ??
        member.employeeName ??
        member.EmployeeName ??
        member.name ??
        member.Name ??
        employee?.fullName ??
        employee?.FullName ??
        "";

    const safeFullName = getSafeText(fullName, "");
    if (safeFullName) return safeFullName;

    const firstName =
        member.firstName ??
        member.FirstName ??
        employee?.firstName ??
        employee?.FirstName ??
        "";

    const lastName =
        member.lastName ??
        member.LastName ??
        employee?.lastName ??
        employee?.LastName ??
        "";

    const name = `${getSafeText(firstName, "")} ${getSafeText(lastName, "")}`.trim();

    return name || "-";
}

function getEmployeeDisplayName(employee, fallback = "-") {
    if (!employee) return fallback;

    const fullName = getSafeText(employee.fullName ?? employee.FullName, "");
    if (fullName) return fullName;

    const parts = [
        employee.firstName ?? employee.FirstName,
        employee.lastName ?? employee.LastName,
    ]
        .map((value) => getSafeText(value, ""))
        .filter(Boolean);

    return parts.length ? parts.join(" ") : fallback;
}

function getTeamDepartmentName(team) {
    if (!team) return "";

    const department = team.department ?? team.Department;

    if (typeof department === "string") {
        return getSafeText(department, "");
    }

    return getSafeText(
        department?.name ??
            department?.Name ??
            team.departmentName ??
            team.DepartmentName,
        "",
    );
}

function getTeamDepartmentColor(team) {
    if (!team) return null;

    const department = team.department ?? team.Department;

    return (
        team.colorHex ??
        team.ColorHex ??
        department?.colorHex ??
        department?.ColorHex ??
        team.departmentColorHex ??
        team.DepartmentColorHex ??
        null
    );
}

export default ClientService;
